import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { listCADModels } from '../../services/cad.service';
import type { CADModel } from '../../types/cad';

const CAD_LOAD_FAILED_ERROR = '__CAD_MODEL_SELECT_LOAD_FAILED__';

interface CADModelSelectProps {
  value?: string;
  onChange: (cadModelId: string | undefined) => void;
  disabled?: boolean;
  helperText?: string;
  label?: string;
}

export const CADModelSelect: React.FC<CADModelSelectProps> = ({
  value = '',
  onChange,
  disabled = false,
  helperText,
  label,
}) => {
  const { t } = useTranslation();
  const [models, setModels] = useState<CADModel[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const selectLabel = label ?? t('cad.modelSelect.label', 'CADモデル');

  useEffect(() => {
    if (disabled) return;

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    listCADModels()
      .then((response) => {
        if (!cancelled) {
          setModels(response.models || []);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : CAD_LOAD_FAILED_ERROR);
          setModels([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [disabled]);

  const completedModels = useMemo(
    () => models.filter((model) => model.status === 'completed'),
    [models],
  );

  const handleChange = (event: SelectChangeEvent<string>) => {
    const nextValue = event.target.value;
    onChange(nextValue || undefined);
  };

  return (
    <Box>
      <FormControl fullWidth size="small" disabled={disabled || isLoading}>
        <InputLabel id="cad-model-select-label">{selectLabel}</InputLabel>
        <Select
          labelId="cad-model-select-label"
          label={selectLabel}
          value={value}
          onChange={handleChange}
          endAdornment={isLoading ? <CircularProgress size={18} sx={{ mr: 3 }} /> : undefined}
        >
          <MenuItem value="" disableRipple>
            {t('cad.modelSelect.none', 'CADモデルを選択しない')}
          </MenuItem>
          {completedModels.map((model) => (
            <MenuItem key={model.id} value={model.id} disableRipple>
              {model.filename}
            </MenuItem>
          ))}
        </Select>
        <FormHelperText>
          {completedModels.length === 0 && !isLoading
            ? t('cad.modelSelect.empty', '利用可能なCADモデルがありません')
            : helperText}
        </FormHelperText>
      </FormControl>
      {error && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {error === CAD_LOAD_FAILED_ERROR
            ? t('cad.modelSelect.loadFailed', 'CADモデルの読み込みに失敗しました')
            : error}
        </Alert>
      )}
    </Box>
  );
};

export default CADModelSelect;
